import {createApi} from '@reduxjs/toolkit/query/react'
import {baseQueryWithReAuth} from '@/store/features/baseRtkQuery'

// Types
export type SourceType = 'sharepoint' | 'confluence'

export interface GetCollectionByIdResponse {
  id: number
  name: string
  site: string
  library: string
  path: string
  recursive: boolean
  cron?: string | null
}

export interface ConfluenceTreeNode {
  id: string
  title: string
  hasChildren: boolean
  children: ConfluenceTreeNode[]
}

export interface SpaceTreeResponse {
  spaceId: string
  spaceKey: string
  spaceName: string
  rootNodes: ConfluenceTreeNode[]
  totalPages: number
}

// Payloads
export interface CreateSharePointPayload {
  name: string
  site: string
  library: string
  path: string
  cron: string
  recursive: boolean
}

export interface UpdateSharePointPayload extends CreateSharePointPayload {
  uuid: string
}

// V2: multi-path Confluence (POST /Collection/confluence)
export interface CreateConfluencePayloadV2 {
  name: string
  spaceKey: string
  items: Array<{ id: string; type: 'folder' | 'file'; recursive?: boolean }>
  cron?: string
}

export interface UpdateConfluencePayloadV2 extends CreateConfluencePayloadV2 {
  uuid: string
}

export interface ConfluenceCollectionItemResponse {
  id: string
  type: string
  recursive: boolean
}

export interface ConfluenceCollectionResponse {
  id: number
  collectionId: string
  name: string
  spaceKey: string
  items: ConfluenceCollectionItemResponse[]
  cron?: string
  status: string
  lastRunAt?: string | null
  nextRunAt?: string | null
}

export interface TriggerConfluenceRunResponse {
  message: string
  id?: number
  status?: string
  nextRunAt?: string
}

export const dynamicCollectionsApiSlice = createApi({
  reducerPath: 'dynamicCollectionsApi',
  baseQuery: baseQueryWithReAuth,
  tagTypes: ['ConfluenceCollection'],
  endpoints: (builder) => ({
    // GET collection by uuid
    getCollectionByUuid: builder.query<GetCollectionByIdResponse, string>({
      query: (uuid) => ({
        url: `/dynamicknowledge/collection/${encodeURIComponent(uuid)}`,
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
      }),
    }),

    // GET confluence space tree
    getConfluenceSpaceTree: builder.query<SpaceTreeResponse, string>({
      query: (spaceKey) => ({
        url: `/collection/dynamic/confluence/spaces/${encodeURIComponent(spaceKey)}/tree`,
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
      }),
    }),

    // GET single Confluence collection by id (for edit)
    getConfluenceCollectionById: builder.query<ConfluenceCollectionResponse, string>({
      query: (id) => ({
        url: `/Collection/confluence/${encodeURIComponent(id)}`,
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
      }),
      providesTags: (result, err, id) => (result ? [{ type: 'ConfluenceCollection', id }] : ['ConfluenceCollection']),
    }),

    // GET list of Confluence collections (confluence_dynamic_collection)
    getConfluenceCollections: builder.query<ConfluenceCollectionResponse[], void>({
      query: () => ({
        url: '/Collection/confluence',
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
      }),
      transformResponse: (response: unknown): ConfluenceCollectionResponse[] => {
        if (Array.isArray(response)) return response;
        if (response && typeof response === 'object') {
          const r = response as Record<string, unknown>;
          if (Array.isArray(r.items)) return r.items as ConfluenceCollectionResponse[];
          if (Array.isArray(r.data)) return r.data as ConfluenceCollectionResponse[];
          if (Array.isArray(r.collections)) return r.collections as ConfluenceCollectionResponse[];
        }
        return [];
      },
      providesTags: (result) =>
        result ? [...result.map((c) => ({ type: 'ConfluenceCollection' as const, id: String(c.id) })), 'ConfluenceCollection'] : ['ConfluenceCollection'],
    }),

    // POST trigger immediate run of a Confluence collection
    triggerConfluenceRun: builder.mutation<TriggerConfluenceRunResponse, number>({
      query: (id) => ({
        url: `/Collection/confluence/${id}/run`,
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
      }),
      invalidatesTags: ['ConfluenceCollection'],
    }),

    // POST create sharepoint collection
    createSharePointCollection: builder.mutation<any, CreateSharePointPayload>({
      query: (body) => ({
        url: `/collection/dynamic/sharepoint`,
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body,
      }),
    }),

    // POST create confluence collection V2 (multi-path: /Collection/confluence)
    createConfluenceCollectionV2: builder.mutation<ConfluenceCollectionResponse, CreateConfluencePayloadV2>({
      query: (body) => ({
        url: `/Collection/confluence`,
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: {
          name: body.name,
          spaceKey: body.spaceKey,
          items: body.items,
          cron: body.cron,
        },
      }),
    }),

    // PUT update confluence collection V2 (multi-path)
    updateConfluenceCollectionV2: builder.mutation<ConfluenceCollectionResponse, UpdateConfluencePayloadV2>({
      query: ({ uuid, ...body }) => ({
        url: `/Collection/confluence/${encodeURIComponent(uuid)}`,
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: {
          name: body.name,
          spaceKey: body.spaceKey,
          items: body.items,
          cron: body.cron,
        },
      }),
    }),

    // PUT update sharepoint collection
    updateSharePointCollection: builder.mutation<any, UpdateSharePointPayload>({
      query: ({ uuid, ...body }) => ({
        url: `/collection/dynamic/sharepoint/${encodeURIComponent(uuid)}`,
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body,
      }),
    }),
  }),
})

export const {
  useGetCollectionByUuidQuery,
  useLazyGetConfluenceSpaceTreeQuery,
  useGetConfluenceCollectionByIdQuery,
  useGetConfluenceCollectionsQuery,
  useTriggerConfluenceRunMutation,
  useCreateSharePointCollectionMutation,
  useCreateConfluenceCollectionV2Mutation,
  useUpdateSharePointCollectionMutation,
  useUpdateConfluenceCollectionV2Mutation,
} = dynamicCollectionsApiSlice